<template>
  <div class="flex h-screen bg-gray-50">
    <!-- Mobile Sidebar Overlay -->
    <div v-if="showMobileSidebar" class="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden" @click="showMobileSidebar = false"></div>
    
    <!-- Sidebar -->
    <div :class="['bg-white border-l border-gray-200 flex flex-col flex-shrink-0 fixed md:relative h-full z-40 transform transition-transform duration-300 ease-in-out', 
                     showMobileSidebar ? 'translate-x-0' : '-translate-x-full md:translate-x-0', 
                     'w-64 md:w-64']">
      <div class="p-4 md:p-6">
        <div class="flex items-center justify-between mb-6">
          <h2 class="text-lg md:text-xl font-bold text-gray-800">المكتبة القانونية</h2>
          <button @click="showMobileSidebar = false" class="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <X class="w-5 h-5 text-gray-600" />
          </button>
        </div>
        
        <!-- Search -->
        <div class="mb-6">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="بحث في القوانين..."
            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <!-- Filters -->
        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">الفئة</label>
            <select
              v-model="selectedCategory"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option v-for="category in categories" :key="category.id" :value="category.id">
                {{ category.name }}
              </option>
            </select>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">نوع الوثيقة</label>
            <select
              v-model="selectedType"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">جميع الأنواع</option>
              <option value="قانون">قانون</option>
              <option value="نظام">نظام</option>
              <option value="تعليمات">تعليمات</option>
              <option value="قرار">قرار</option>
            </select>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">البلد</label>
            <select
              v-model="selectedCountry"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">جميع البلدان</option>
              <option value="الأردن">الأردن</option>
              <option value="السعودية">السعودية</option>
              <option value="الإمارات">الإمارات</option>
              <option value="مصر">مصر</option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="flex-1 p-4 md:p-6">
      <!-- Mobile Header -->
      <div class="flex items-center justify-between mb-6 md:hidden">
        <h1 class="text-lg font-bold text-gray-800">المكتبة القانونية</h1>
        <button @click="showMobileSidebar = true" class="p-2 rounded-lg hover:bg-gray-100 transition-colors">
          <Filter class="w-5 h-5 text-gray-600" />
        </button>
      </div>

      <div class="max-w-6xl mx-auto">
        <!-- Loading State -->
        <div v-if="isLoading" class="text-center py-12">
          <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p class="text-gray-600 mt-4">جاري تحميل القوانين...</p>
        </div>

        <!-- Documents Grid -->
        <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          <div
            v-for="document in filteredDocuments"
            :key="document.id"
            class="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow cursor-pointer"
            @click="navigateToLawDetails(document.id)"
          >
            <div class="flex items-start justify-between mb-4">
              <div class="flex-1">
                <h3 class="text-lg font-semibold text-gray-800 mb-2">{{ document.title }}</h3>
                <p class="text-sm text-gray-600 mb-3">{{ document.content.substring(0, 100) }}...</p>
              </div>
              <div class="ml-3">
                <FileText class="w-8 h-8 text-blue-500" />
              </div>
            </div>
            
            <div class="flex items-center justify-between mb-3">
              <span class="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                {{ document.documentType }}
              </span>
              <div class="flex items-center text-xs text-gray-500">
                <Star class="w-3 h-3 ml-1 text-yellow-400" />
                <span>{{ document.rating || 0 }}</span>
              </div>
            </div>

            <div class="flex items-center text-xs text-gray-500 mb-3">
              <MapPin class="w-3 h-3 ml-1" />
              <span>{{ document.countryCode }}</span>
              <span class="mx-2">•</span>
              <span>{{ document.articlesCount || 0 }} مواد</span>
              <span class="mx-2">•</span>
              <span>{{ document.year || 'N/A' }}</span>
            </div>

            <div class="flex items-center justify-between">
              <span class="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                {{ document.downloads || 0 }} تحميل
              </span>
              <button class="text-blue-600 hover:text-blue-800 text-sm font-medium">
                عرض التفاصيل
              </button>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-if="!isLoading && filteredDocuments.length === 0" class="text-center py-12">
          <FileText class="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p class="text-gray-600">لم يتم العثور على وثائق مطابقة للبحث</p>
          <button
            @click="fetchDocuments"
            class="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            إعادة المحاولة
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { FileText, MapPin, Star, X, Filter } from 'lucide-vue-next'
import { apiService } from '../services/api'
import type { LibraryDocument } from '../types'

interface Category {
  id: string
  name: string
}

const router = useRouter()
const searchQuery = ref('')
const selectedCategory = ref('all')
const selectedType = ref('')
const selectedCountry = ref('')
const isLoading = ref(false)
const showMobileSidebar = ref(false)
const documents = ref<LibraryDocument[]>([])

const categories: Category[] = [
  { id: 'all', name: 'الكل' },
  { id: 'civil', name: 'القانون المدني' },
  { id: 'criminal', name: 'القانون الجزائي' },
  { id: 'commercial', name: 'القانون التجاري' },
  { id: 'labor', name: 'قانون العمل' },
  { id: 'family', name: 'القانون الأسري' }
]

// Fetch documents from API
const fetchDocuments = async () => {
  isLoading.value = true
  try {
    console.log('Fetching laws from backend...')
    const response = await apiService.getLaws(selectedCountry.value)
    console.log('Received response:', response)
    documents.value = response.documents
    console.log('Documents loaded:', documents.value.length)
  } catch (error) {
    console.error('Error fetching documents:', error)
    // Show error message to user
    documents.value = []
  } finally {
    isLoading.value = false
  }
}


// Load documents on component mount
onMounted(() => {
  fetchDocuments()
})

const filteredDocuments = computed(() => {
  return documents.value.filter(doc => {
    const matchesSearch = doc.title.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
                         doc.content.toLowerCase().includes(searchQuery.value.toLowerCase())
    const matchesCategory = selectedCategory.value === 'all' || doc.category === selectedCategory.value
    const matchesType = !selectedType.value || doc.documentType === selectedType.value
    const matchesCountry = !selectedCountry.value || doc.countryCode === selectedCountry.value
    
    return matchesSearch && matchesCategory && matchesType && matchesCountry
  })
})

const navigateToLawDetails = (lawId: string) => {
  console.log('Navigating to law details for ID:', lawId)
  router.push(`/law/${lawId}`)
}
</script>
